export interface TbidFields {
    company?: string;
    id?: string;
    orgId?: string;
    relationship?: string;
    role?: string;
}
export interface GtmTbid {
    tb: TbidFields;
}
export interface GtmPageLevelEvent {
    appCost: string;
    appType: string;
    loginStatus: string;
    userState: string;
    id: GtmTbid;
}

export interface GtmAnalyticsEvent {
    event: string;
    timestamp: number;
    eventContext: string;
}

export interface GtmSearchClickEvent extends GtmAnalyticsEvent {
    searchTerm: string;
    searchResultName: string;
    clickUrl: string;
    ctaClick: boolean;
    clickText: string;
    elementType: string;
    itemTitle?: string;
}
export interface GtmSearchEvent {
    event: string;
    searchResultsApps: number;
    searchResultsConsultants: number;
    searchTerm: string;
    searchResultName: string;
}
export interface GtmFilterSearchEvent {
    event: string;
    search_term: string;
    search_result_count: number;
    search_type: string;
}

export type GTMVersion = 'ga' | 'ga4';

export type GTMBaseEvent = {
    topic: string;
    elementType: string;
    timestamp: number;
    gtm?: GTMVersion;
    [key: string]: unknown;
};

// GA4 Interfaces
export interface GA4AnalyticsEvent {
    event: string;
}

export interface GA4PageLevelEvent {
    locale: string;
    login_status: string;
    page_path: string;
    page_path_with_query: string;
    page_referrer: string;
    referring_hostname: string;
    tbid_company: string;
    tbid_org_id: string;
    tbid_relationship: string;
    tbid_role: string;
    user_id: string;
    listing_url?: string;
    listing_id?: string;
}

export interface GA4ListingDetailClickEvent extends GA4AnalyticsEvent {
    click_text: string;
    click_url: string;
    element_title: string;
    element_type: string;
    internal_driver: string;
    lp_detail_interaction: boolean;
    app_cost: string;
    app_type: string;
    featured: boolean;
    recommended: boolean;
    ignore_hover: boolean;
    listing_id: string;
    internal_article: boolean;
    listing_name: string;
    listing_type: string;
    listing_url: string;
    hover_intent: boolean;
    internal_source: string;
    page_type: string;
}

export interface GA4ContentClickEvent extends GA4AnalyticsEvent {
    click_text?: string;
    click_url?: string;
    element_title?: string;
    element_type: string;
    content_category?: string;
    internal_driver?: string;
    listing_flow?: string;
    ctaClick?: boolean;
}
export interface GA4TileClickEvent extends GA4AnalyticsEvent {
    click_text: string;
    click_url: string;
    element_title: string;
    element_type: string;
    lp_detail_interaction: boolean;
    app_cost?: string;
    app_type?: string;
    featured: boolean;
    recommended: boolean;
    ignore_hover: boolean;
    listing_id: string;
    internal_article: boolean;
    listing_name: string;
    listing_type: string;
    listing_url: string;
    hover_intent: boolean;
    internal_source: string;
    page_type: string;
}
export interface GA4TryButtonClickEvent extends GA4AnalyticsEvent {
    click_text: string;
    ctaClick: boolean;
    click_url: string;
    element_title: string;
    element_type: string;
    content_category: string;
    listing_flow: string;
    internal_driver: string;
}
export interface GA4SearchResultClickEvent extends GA4TileClickEvent {
    search_term: string;
    search_category: string;
    search_result_clicks: number;
    app_results: number;
    consultant_results: number;
}
export interface GA4TileViewEvent extends GA4AnalyticsEvent {
    event: string;
    element_title: string;
    element_type: string;
    lp_detail_interaction: boolean;
    app_cost?: string;
    app_type?: string;
    featured: boolean;
    recommended: boolean;
    ignore_hover: boolean;
    listing_id: string;
    internal_article: boolean;
    listing_name: string;
    listing_type: string;
    listing_url: string;
    hover_intent: boolean;
    internal_source: string;
    page_type: string;
}
export interface GA4SearchClickEvent extends GA4AnalyticsEvent {
    click_text: string;
    element_type: string;
    search_term: string;
    search_type: string;
    search_result_count?: number;
    search_category: string;
    app_results?: number;
    consultant_results?: number;
}

export interface GA4SearchEvent extends GA4AnalyticsEvent {
    search_term: string;
    search_type: string;
    search_result_count: number;
    search_category: string;
    app_results?: number;
    consultant_results?: number;
}

export interface GA4FilterClickEvent extends GA4SearchClickEvent {
    filter_label: string;
    filter_action: string;
    app_results: number;
    consultant_results: number;
}

export interface GA4LoginNavClickEvent extends GA4AnalyticsEvent {
    nav_level: string;
    nav_item: string;
    click_text: string;
    click_url: string;
    internal_driver: string;
    element_type: string;
    nav_type: string;
}

export interface GA4HeaderNavLinkClickEvent extends GA4AnalyticsEvent {
    nav_level: string;
    nav_item: string;
    click_text: string;
    click_url: string;
    internal_driver: string;
    element_type: string;
    nav_type: string;
}

export interface AuthenticationEvent extends GA4AnalyticsEvent {
    authentication_method: string;
    authentication_type: string;
}

export interface GA4CarouselClickEvent extends GA4AnalyticsEvent {
    click_text: string;
    click_url: string;
    element_type: string;
    lp_detail_interaction: boolean;
    app_cost: string;
    app_type: string;
    featured: boolean;
    recommended: boolean;
    ignore_hover: boolean;
    internal_article: boolean;
    internal_source: string;
    listing_id: string;
    listing_name: string;
    listing_type: string;
    listing_url: string;
    hover_intent: boolean;
    page_type: string;
}

export type GTMBaseEventWithTiles = {
    tiles: {
        name: string;
        title?: string;
    };
} & GTMBaseEvent;

export abstract class GtmEventType {
    abstract canHandle(event: GTMBaseEvent): boolean;
    abstract getBody(
        // @ts-expect-error * Automated Type Error Fix
        event,
    ):
        | GtmAnalyticsEvent
        | GA4AnalyticsEvent
        | GtmPageLevelEvent
        | GA4PageLevelEvent
        | GtmSearchEvent
        | GA4SearchEvent
        | GtmFilterSearchEvent
        | GA4ContentClickEvent
        | GA4TileClickEvent
        | GA4HeaderNavLinkClickEvent
        | GA4CarouselClickEvent;

    // @ts-expect-error * Automated Type Error Fix
    isValidEvent(__event): boolean {
        return true;
    }
}
